import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, AlertCircle, CheckCircle, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const ComplianceCalendarPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${apiUrl}/api/compliance/calendar/upcoming?days=180`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      setUpcomingEvents({
        overdue: data.overdue || [],
        dueSoon: data.dueSoon || [],
        upcoming: data.upcoming || []
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  const totalEvents = upcomingEvents.overdue.length + upcomingEvents.dueSoon.length + upcomingEvents.upcoming.length;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/compliance/vault')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Compliance Vault
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Compliance Calendar</h1>
          <p className="text-gray-600 mt-1">Track important deadlines and submissions</p>
        </div>

        {/* Alert if overdue */}
        {upcomingEvents.overdue.length > 0 && (
          <AlertBanner
            type="error"
            message={`You have ${upcomingEvents.overdue.length} overdue compliance item(s). Take action immediately!`}
            className="mb-6"
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Overdue</p>
                <p className="text-3xl font-bold text-red-600">{upcomingEvents.overdue.length}</p>
              </div>
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Due Soon</p>
                <p className="text-3xl font-bold text-orange-600">{upcomingEvents.dueSoon.length}</p>
              </div>
              <Clock className="w-12 h-12 text-orange-500" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Upcoming</p>
                <p className="text-3xl font-bold text-blue-600">{upcomingEvents.upcoming.length}</p>
              </div>
              <Calendar className="w-12 h-12 text-blue-500" />
            </div>
          </div>
        </div>

        {totalEvents === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">No upcoming compliance deadlines at this time.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Overdue Section */}
            {upcomingEvents.overdue.length > 0 && (
              <EventSection
                title="Overdue"
                events={upcomingEvents.overdue}
                color="red"
                icon={AlertCircle}
              />
            )}

            {/* Due Soon Section */}
            {upcomingEvents.dueSoon.length > 0 && (
              <EventSection
                title="Due Soon (Next 7 Days)"
                events={upcomingEvents.dueSoon}
                color="orange"
                icon={Clock}
              />
            )}

            {/* Upcoming Section */}
            {upcomingEvents.upcoming.length > 0 && (
              <EventSection
                title="Upcoming"
                events={upcomingEvents.upcoming}
                color="blue"
                icon={Calendar}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Event Section Component
const EventSection = ({ title, events, color, icon: Icon }) => {
  const colorClasses = {
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' }
  };

  const classes = colorClasses[color];

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <Icon className={`w-6 h-6 ${classes.text}`} />
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <span className={`px-3 py-1 ${classes.bg} ${classes.text} rounded-full text-sm font-semibold`}>
          {events.length}
        </span>
      </div>

      <div className="space-y-4">
        {events.map((event, index) => (
          <EventCard key={index} event={event} color={color} />
        ))}
      </div>
    </div>
  );
};

// Event Card Component
const EventCard = ({ event, color }) => {
  const getDaysInfo = () => {
    const due = new Date(event.dueDate);
    const now = new Date();
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    
    if (diff < 0) return `${Math.abs(diff)} days overdue`;
    if (diff === 0) return 'Due today';
    if (diff === 1) return 'Due tomorrow';
    return `${diff} days remaining`;
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">{event.eventType}</h3>
          <p className="text-sm text-gray-600">Due: {new Date(event.dueDate).toLocaleDateString()}</p>
        </div>
        <div className={
          `px-3 py-1 rounded-full text-sm font-semibold ${
            color === 'red' ? 'bg-red-100 text-red-700' :
            color === 'orange' ? 'bg-orange-100 text-orange-700' :
            'bg-blue-100 text-blue-700'
          }`
        }>
          {getDaysInfo()}
        </div>
      </div>

      {event.projectId && (
        <p className="text-xs text-gray-500 mb-3">Project: {event.projectId}</p>
      )}

      <Button
        size="sm"
        className="text-white"
        style={{ background: '#14B8A6' }}
      >
        Take Action
      </Button>
    </div>
  );
};

export default ComplianceCalendarPage;